// Design A ("Corporate Band") Purchase -- used ONLY for Print Preview (via
// PurchasePrint.jsx) when this company's institution_profile.print_template
// is 'design_a'. Mirrors backend/src/pdfTemplates/designA/purchaseAdapter.js
// exactly. PurchaseVoucherBody.jsx (legacy, still used for the live
// entry-screen preview and for 'legacy' print_template) is untouched.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, formatToCompanyTimezone, formatDateOnly } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { formatPercent } from "../../../../../lib/designANumberFormat";
import { purchaseRecordnWithDetailsApi } from "../../../../../apiservice/purchase";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi, getBankAccountByIdsApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

const PURCHASE_ROW_BUDGETS = { single: 16, first: 24, middle: 30, last: 14 };

// Fixed-layout column widths -- same methodology as salesInvoiceAdapter.js/
// SalesVoucherBodyDesignA.jsx. These were initially sized too small for
// Purchase's own (longer) header text -- "Selling Price", "Quantity",
// "Discount %", "Expiry Date" all clipped with "…" on a real print.
// Recomputed against the same per-character safety margin already
// validated on Sales Invoice (~8px/char + 16px padding). "per" also
// widened since it holds a full unit NAME here, not just a short symbol.
// See purchaseAdapter.js's comment for why even the simplest case now
// hits the item name's Math.max(20, …) floor -- an inherent consequence
// of how many columns this voucher type needs at once, not a bug.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 28, rate: 100, sellingPrice: 120, per: 66, qty: 80, discount: 96, batchNo: 80, expiryDate: 104, gp: 42, tax: 54, total: 88 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

export default function PurchaseVoucherBodyDesignA({ pur_id, showStamp = false, showSignature = false }) {
  const [record, setRecord] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const [paymentBankAccount, setPaymentBankAccount] = useState(null);
  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!pur_id) return;
    purchaseRecordnWithDetailsApi({ pur_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setRecord(res.data[0]);
    });
  }, [pur_id]);

  // A Purchase pays money OUT to the vendor, so its own voucher_transactions
  // rows use from_acc_id (not to_acc_id like Sales/Purchase Return, where
  // money comes IN) -- a cheque payment on the same voucher still uses
  // to_acc_id though, so both fields are checked. Prefer whichever bank
  // account this voucher's own payment actually used over the branch-wide
  // "primary" one (see getBankAccountByIds's comment in
  // backend/src/controllers/accounts.js).
  useEffect(() => {
    if (!record) return;
    const accIds = (record.trans || []).map((t) => t.from_acc_id || t.to_acc_id).filter(Boolean);
    if (!accIds.length) return;
    getBankAccountByIdsApi(accIds).then((res) => setPaymentBankAccount(res?.data?.data || null)).catch(() => {});
  }, [record]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!record) return null;

  const bankDetails = bankAccountToConfig(paymentBankAccount || primaryBankAccount);

  const currency = record?.currency || authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isPharmacy = institutionData?.company_type === "Pharmacy";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const isIndividual = record.is_cal_type === "individual";
  const items = (record.details || []).map((item) => ({
    ...item,
    qty_display: `${formatPercent(item.item_qty)} ${item.unit_symbol}` + (item.conversion > 1 ? `, ${formatPercent(item.retail_qty)} ${item.base_unit_name}` : ""),
  }));

  const effectiveRateByItem = items.reduce((acc, d) => {
    const key = d.item_id;
    const qty = Number(d.item_qty || 0);
    const rate = Number(d.item_rate || 0);
    const agg = acc[key] || { qty: 0, value: 0 };
    agg.qty += qty;
    agg.value += qty * rate;
    acc[key] = agg;
    return acc;
  }, {});
  const getEffectiveItemRate = (d) => {
    const agg = effectiveRateByItem[d.item_id];
    return agg && agg.qty > 0 ? agg.value / agg.qty : Number(d.item_rate || 0);
  };
  const gpPercent = (retailRate, itemRate) => {
    const retail = Number(retailRate || 0);
    const rate = Number(itemRate || 0);
    if (!retail) return "0";
    return (((retail - rate) / retail) * 100).toFixed(2).replace(/\.00$/, "");
  };

  // Matches legacy's own condition exactly (PurchaseVoucherBody.jsx): show
  // Batch No/Expiry Date whenever this voucher actually has batch data, or
  // it's a pharmacy that hasn't explicitly turned batch tracking off --
  // not just "is this a pharmacy account" alone.
  const showBatchColumns =
    (isPharmacy && authInfo?.enable_batch_number !== "no") ||
    items.some((d) => d.batch_name);

  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.sellingPrice + COL_WIDTH.per + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.discount : 0) +
    (showBatchColumns ? COL_WIDTH.batchNo + COL_WIDTH.expiryDate : 0) +
    COL_WIDTH.gp + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.total;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [{ label: "#", width: `${COL_WIDTH.hash}px` }, { label: "Item Name" }];
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Selling Price", align: "right", width: `${COL_WIDTH.sellingPrice}px` });
  columns.push({ label: "Per", align: "right", width: `${COL_WIDTH.per}px` });
  columns.push({ label: "Quantity", align: "right", width: `${COL_WIDTH.qty}px` });
  if (isIndividual) columns.push({ label: "Discount %", align: "right", width: `${COL_WIDTH.discount}px` });
  if (showBatchColumns) {
    columns.push({ label: "Batch No", width: `${COL_WIDTH.batchNo}px` });
    columns.push({ label: "Expiry Date", width: `${COL_WIDTH.expiryDate}px` });
  }
  columns.push({ label: "GP%", align: "right", width: `${COL_WIDTH.gp}px` });
  if (isIndividual && !isGSTDisabled) columns.push({ label: `${taxLabel}%`, align: "right", width: `${COL_WIDTH.tax}px` });
  columns.push({ label: isIndividual ? "Total" : "Amount", align: "right", width: `${COL_WIDTH.total}px` });

  const rows = items.map((d, i) => {
    const cells = [String(i + 1), <ItemNameCell key="n" name={d.item_name} maxChars={itemMaxChars} />];
    cells.push(<span className="da-num-cell" key="r">{Number(d.item_rate || 0).toFixed(2)}</span>);
    cells.push(<span className="da-num-cell" key="sp">{Number(d.retail_rate || 0).toFixed(2)}</span>);
    cells.push(isIndividual ? d.unit_name : d.per_unit_symbol);
    cells.push(<span className="da-num-cell" key="q">{isIndividual ? Number(d.item_qty || 0).toFixed(2).replace(/\.00$/, "") : d.qty_display}</span>);
    if (isIndividual) cells.push(<span className="da-num-cell" key="d">{Number(d.item_discount_per || 0).toFixed(2).replace(/\.00$/, "")}</span>);
    if (showBatchColumns) {
      cells.push(<span className="da-col-batch" key="b">{d.batch_name || "-"}</span>);
      cells.push(d.batch_expiry ? formatDateOnly(d.batch_expiry) : "-");
    }
    cells.push(<span className="da-num-cell" key="gp">{gpPercent(d.retail_rate, getEffectiveItemRate(d))}</span>);

    let rawAmount;
    if (isIndividual) {
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{Number(d.item_tax_per || 0).toFixed(2).replace(/\.00$/, "")}</span>);
      let total = (d.item_qty || 0) * (d.item_rate || 0);
      total -= (Number(d.item_discount_per || 0) / 100) * total;
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: false, rawAmount };
  });

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number(d.item_qty || 0);
      const rate = Number(d.item_rate || 0);
      const base = qty * rate;
      const discountAmt = (Number(d.item_discount_per || 0) / 100) * base;
      const afterDiscount = base - discountAmt;
      const taxAmt = (Number(d.item_tax_per || 0) / 100) * afterDiscount;
      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;
      acc.totalQty += qty;
      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0, totalQty: 0 }
  );
  const totalTax = isIndividual ? totals.taxTotal : Number(record.total_tax || 0);
  const serviceCost = Number(record.other_service_cost || 0);
  const grandTotalForWords = (isIndividual ? totals.grandTotal : record.total_amount || 0) + serviceCost;

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(isIndividual ? totals.subTotal : record.sub_total) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(isIndividual ? totals.discountTotal : record.total_discount)}`, credit: true });

  let taxSummary = null;
  const isIndiaGst = authInfo?.gst_enabled === "yes" && isIndia;
  if (isIndiaGst) {
    if (record.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormat(record.cgst_amount) });
      totalsRows.push({ label: "SGST", value: numberFormat(record.sgst_amount) });
      taxSummary = { mode: "cgst_sgst", rows: [{ rate: `${record.item_tax_per || ""}%`, taxable: numberFormat(totals.subTotal - totals.discountTotal), cgst: numberFormat(record.cgst_amount), sgst: numberFormat(record.sgst_amount) }] };
    }
    if (record.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormat(record.igst_amount) });
      taxSummary = { mode: "igst", rows: [{ rate: `${record.item_tax_per || ""}%`, taxable: numberFormat(totals.subTotal - totals.discountTotal), igst: numberFormat(record.igst_amount) }] };
    }
    if (record.gst_calculated === "no") totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(totalTax) });
  } else if (!isIndia) {
    totalsRows.push({ label: `Total ${taxLabel}`, value: numberFormat(totalTax) });
  }
  if (serviceCost > 0) totalsRows.push({ label: "Additional Expense", value: numberFormat(serviceCost) });


  const useBranch = !!authInfo?.branch_info;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  const config = {
    docTitle: "PURCHASE INVOICE",
    docNumber: record.pur_voucher_no,
    institution: { logoUrl: useBranch ? institution?.branch_logo : institution?.logo, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Supplier",
      billTo: {
        name: record.acc_name,
        lines: [
          record.acc_code ? `Supplier Code: ${record.acc_code}` : null,
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: +${record.contact_no}` : null,
          record.licence_no ? `License No: ${record.licence_no}` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: { name: companyName, lines: [institutionData?.street_address || null, institutionData?.phone_no ? `+${institutionData.phone_no}` : null].filter(Boolean) },
      metaLabel: "Voucher Details",
      meta: [
        { label: "Expense Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        { label: "Expense By", value: (record.full_name || "").split(" ")[0] },
        ...(institutionData?.vat_license ? [{ label: taxRegLabel, value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: totals.totalQty,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat((isIndividual ? totals.grandTotal : record.total_amount || 0) + serviceCost),
    paidValue: numberFormat(record.paid_total || record.paid_amount || 0),
    dueValue: numberFormat(record.due_total || record.due_amount || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotalForWords, { country, currency }),
    bankDetails,
    taxSummary,
    terms: institutionData?.invoice_default_terms || record.narration || "-",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: null,
    rowBudgets: PURCHASE_ROW_BUDGETS,
  };

  return <VoucherBodyDesignA config={config} />;
}
